import React from 'react';

// external libs

import { Route, Switch } from 'react-router-dom';

// common

import PrivateRoute from '../components/navigation/PrivateRoute';
import { configs } from '../constants/configs/configs';
import states from '../constants/configs/states';
import { ALL_USER_ROLES } from '../constants/roles/userRoleTypes';
import RouteRenderEnvironment from './RouteRenderEnvironment';

// components

import AccessCodeContainer from '../components/accessCode/AccessCodeContainer';
import ForgotEmailContainer from '../components/anonymous/forgotEmail/ForgotEmailContainer';
import HomeContainer from '../components/anonymous/home/HomeContainer';
import PreOEPLandingContainer from '../components/anonymous/pre-OEP_landing/pre-oep_landingContainer';
import EDESearchAndSelfDesignationContainer from '../components/broker/edeSearchAndDesignation/EDESearchAndSelfDesignationContainer';
import UpcomingDueDateContainer from '../components/charts/UpcomingDueDateContainer';
import SecureInbox from '../components/individualPortal/secureInbox/SecureInbox';
import BrokerConnectContainer from '../containers/BrokerConnectContainer/BrokerConnectContainer.js';
import BrokerContainer from '../containers/broker/BrokerContainer';
import CrmContainer from '../containers/crm/CrmContainer';
import DelegationContainer from '../containers/delegation/DelegationContainer';
import DocumentsVerificationContainer from '../containers/documentsVerificationContainer/DocumentsVerificationContainer';
import EntityExportContainer from '../containers/entity/EntityExportContainer';
import EntityTransportBOBContainer from '../containers/entity/EntityTransportBOBContainer';
import FallbackContainer from '../containers/fallback/FallbackContainer';
import CommunicationPreferencesContainer from '../containers/individualPortal/CommunicationPreferencesContainer';
import IndividualPortalContainer from '../containers/individualPortal/IndividualPortalContainer';
import TobaccoContainer from '../containers/individualPortal/tobacco/TobaccoContainer';
import LceContainer from '../containers/lce/LceContainer';
import SsapContainer from '../containers/ssap/SsapContainer';
import TicketMgmtContainer from '../containers/ticketMgmtPortal/TicketMgmtContainer';
import RIDPContainer from '../modules/ridp/index';
import CapApplicationsContainer from '../modules/members/applications';

import ArchiveHouseholdContainer from '../modules/archive-household';
import { IssuerEdiTestingContainer, IssuerEdiTestingDetailsContainer } from '../modules/issuer-edi-testing';
import { SecureInboxContainer } from '../modules/secure-inbox';
// types

import type { STATES_TYPE } from '../constants/configs/states';

// Providers

import { getRouteAndState } from '../components/anonymous/home/utils';
import ApplicationEligibility from '../modules/crm-application-eligibility/ApplicationEligibility';
import VerificationsContainer from '../modules/dmi-verifications';
import AddMemberContainer from '../modules/members/add-member/AddMemberContainer';
import EnrollmentContainer from '../modules/members/enrollments/EnrollmentContainer';
import AccountActivityContainer from '../modules/members/account-activity/AccountActivityContainer';

interface Props {
    stateCode: STATES_TYPE;
    ticketEnabled: boolean;
    leadPresent: string | null;
    userData: {
        role: string;
        activeModuleName: string;
    };
}

function AppRoutes(props: Props) {
    const {
        routes: {
            ACCESS_CODE,
            ARCHIVE_HOUSEHOLD,
            BROKER_CONNECT,
            COMMUNICATION_PREFERENCES_AC,
            CRM_APPLICATION_ELIGIBILITY,
            CRM_SEARCH,
            DELEGATION_OTHER,
            EDE_ID_PROOFING,
            MEMBER_ENROLLMENT_LIST,
            ENTITY_EXPORT_BOB,
            ENTITY_TRANSFER_BOB,
            FALLBACK,
            FORGOT_EMAIL,
            HOME,
            INDPORTAL,
            ISSUER_EDI_TESTING,
            ISSUER_EDI_TESTING_TEST_DETAILS,
            LCE,
            NEW_INBOX_HOME,
            PRE_OEP_LANDING,
            REPORT_DUEDATE_CHARTS,
            RIDP,
            SECURE_INBOX,
            ACCOUNT_ACTIVITY,
            SSAP,
            TICKET_BROKER,
            TICKET_BROKER_DETAIL,
            TICKET_MGMT,
            TOBACCO,
            VERIFICATION_DOCUMENTS,
            ADD_MEMBER,
            DMI_VERIFICATIONS,
            CAP_APPLICATIONS,
        },
    } = configs;

    const { stateCode, ticketEnabled, userData, leadPresent } = props;

    const isNevada = stateCode === states.NEVADA;
    const isNewJersey = stateCode === states.NEW_JERSEY;
    const isIdaho = stateCode === states.IDAHO;
    const isPennie = stateCode === states.PENNSYLVANIA;
    const isVirginia = stateCode === states.VIRGINIA;
    const isGeorgia = stateCode === states.GEORGIA;
    const isNewMexico = stateCode === states.NEW_MEXICO;
    const isBrokerConnectAllowed = isNevada || isPennie || isVirginia || isGeorgia || isNewMexico;
    const isPreOEPLandingAllowed = isGeorgia || isVirginia;
    const canViewMemberPortal = userData?.activeModuleName.toUpperCase() === ALL_USER_ROLES.INDIVIDUAL;

    const updatedPath = getRouteAndState(leadPresent, null, stateCode);
    const REDIRECTED_LANDING_PAGE_PATH = updatedPath.route;

    return (
        <Switch>
            <Route
                exact={true}
                path={VERIFICATION_DOCUMENTS}
                component={DocumentsVerificationContainer}
            />
            <Route path={ARCHIVE_HOUSEHOLD} exact component={ArchiveHouseholdContainer} />
            <PrivateRoute
                path={HOME}
                exact
                isAllowed={isGeorgia || isIdaho || isNevada || isNewJersey || isPennie || isVirginia || isNewMexico}
                component={HomeContainer}
                redirectPath={REDIRECTED_LANDING_PAGE_PATH}
            />
            <Route path={COMMUNICATION_PREFERENCES_AC} exact component={CommunicationPreferencesContainer} />
            <Route path={ACCESS_CODE} exact component={AccessCodeContainer} />
            <Route path={FALLBACK} exact component={FallbackContainer} />
            <Route path={LCE} exact component={LceContainer} />
            <Route path={SSAP} exact component={SsapContainer} />
            <Route path={DELEGATION_OTHER} exact component={DelegationContainer} />
            <Route path={TOBACCO} exact component={TobaccoContainer} />
            <Route path={CRM_SEARCH} exact component={CrmContainer} />
            <Route
                path={EDE_ID_PROOFING}
                render={props => <RouteRenderEnvironment component={EDESearchAndSelfDesignationContainer} {...props} />}
            />
            <Route path={FORGOT_EMAIL} component={ForgotEmailContainer} />
            <PrivateRoute exact={true} path={TICKET_BROKER} isAllowed={ticketEnabled} component={BrokerContainer} />
            <Route path={ENTITY_EXPORT_BOB} component={EntityExportContainer} />
            <Route path={ENTITY_TRANSFER_BOB} exact component={EntityTransportBOBContainer} />
            <Route path={TICKET_BROKER_DETAIL} exact component={BrokerContainer} />
            <Route
                path={NEW_INBOX_HOME}
                exact
                render={props => <RouteRenderEnvironment component={SecureInbox} {...props} />}
            />
            <Route path={TICKET_MGMT} component={TicketMgmtContainer} />
            <Route path={SECURE_INBOX} exact component={SecureInboxContainer} />
            <Route path={ACCOUNT_ACTIVITY} exact component={AccountActivityContainer} />
            <PrivateRoute
                exact={false}
                path={BROKER_CONNECT}
                isAllowed={isBrokerConnectAllowed}
                component={BrokerConnectContainer}
            />
            <Route exact={true} path={ISSUER_EDI_TESTING} component={IssuerEdiTestingContainer} />
            <Route exact={true} path={ISSUER_EDI_TESTING_TEST_DETAILS} component={IssuerEdiTestingDetailsContainer} />
            <PrivateRoute
                path={INDPORTAL}
                exact={false}
                isAllowed={canViewMemberPortal}
                component={IndividualPortalContainer}
                redirectPath={FALLBACK}
            />
            <Route path={CRM_APPLICATION_ELIGIBILITY} component={ApplicationEligibility} />
            <PrivateRoute
                path={PRE_OEP_LANDING}
                exact
                isAllowed={isPreOEPLandingAllowed}
                component={PreOEPLandingContainer}
            />
            <Route path={REPORT_DUEDATE_CHARTS} component={UpcomingDueDateContainer} />
            <Route path={ADD_MEMBER} component={AddMemberContainer} />
            <Route path={RIDP} render={props => <RouteRenderEnvironment component={RIDPContainer} {...props} />} />
            <Route path={DMI_VERIFICATIONS} component={VerificationsContainer} />
            <Route path={MEMBER_ENROLLMENT_LIST} component={EnrollmentContainer} />
            <Route path={CAP_APPLICATIONS} component={CapApplicationsContainer} />
        </Switch>
    );
}

export default AppRoutes;
